import { Checkbox } from "@/components/ui/checkbox";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { AlertTriangle } from "lucide-react";
import { memo } from "react";

type UnmappedItem = SalonStockerHttpTypes.StoreExternalCheckoutUnmappedItem;

export interface UnmappedItemsWarningProps {
  /** Line items that cannot be transferred to the supplier's store. */
  items: UnmappedItem[];
  /** Whether the salon has acknowledged that these items will be dropped. */
  acknowledged: boolean;
  /** Acknowledgement toggle callback. */
  onAcknowledgedChange: (acknowledged: boolean) => void;
}

/**
 * Warns that some cart items cannot be checked out directly with the supplier
 * and requires the salon to acknowledge before continuing. Rendered inside the
 * external-checkout interstitial when the backend returns unmapped items.
 */
export const UnmappedItemsWarning = memo(function UnmappedItemsWarning({
  items,
  acknowledged,
  onAcknowledgedChange
}: UnmappedItemsWarningProps) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex gap-3">
        <AlertTriangle
          className="h-5 w-5 flex-shrink-0 text-amber-600"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-amber-900">
            {items.length === 1
              ? "1 item won't transfer"
              : `${items.length} items won't transfer`}
          </p>
          <p className="mt-1 text-sm text-amber-800">
            These items aren&apos;t available for direct checkout with this
            supplier and won&apos;t be included:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800">
            {items.map((item, index) => (
              <li key={item.variant_id ?? index}>
                {item.title}
                {item.quantity > 1 ? ` × ${item.quantity}` : ""}
              </li>
            ))}
          </ul>
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-amber-900">
            <Checkbox
              checked={acknowledged}
              onCheckedChange={(value) => onAcknowledgedChange(value === true)}
              aria-label="Continue without the items that won't transfer"
            />
            Continue without these items
          </label>
        </div>
      </div>
    </div>
  );
});
